import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Eye, EyeOff } from "lucide-react";

export default function ClaimAccount() {
  const { token } = useParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { setAuthData } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setError(null);
    setLoading(true);
    try {
      const data = await api.post(`/auth/claim/${token}`, { password });
      setAuthData(data);
      navigate("/onboarding/consent", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-lv-bg flex items-center justify-center px-6" data-testid="claim-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <Link to="/" className="font-serif text-2xl text-cream tracking-[0.25em]">LV PASS</Link>
          <p className="text-xs tracking-[0.3em] uppercase text-lv-muted mt-4">Claim Your LV Pass</p>
        </div>
        <p className="text-sm text-sand/80 text-center mb-8 leading-relaxed">
          Create a password to set up your account and access your membership.
        </p>
        {error && <div className="mb-6 p-4 border border-lv-inactive/30 bg-lv-inactive/10 text-sand text-sm" data-testid="claim-error">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-xs tracking-[0.2em] uppercase text-lv-muted mb-2" htmlFor="claim-pw">Choose Password</label>
            <div className="relative">
              <input id="claim-pw" type={showPw ? "text" : "password"} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className="lv-input pr-12" placeholder="Min. 8 characters" required minLength={8} data-testid="claim-password" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-lv-muted hover:text-cream transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Toggle password visibility">
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs tracking-[0.2em] uppercase text-lv-muted mb-2" htmlFor="claim-confirm">Confirm Password</label>
            <input id="claim-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="lv-input" placeholder="Re-enter password" required data-testid="claim-confirm" />
          </div>
          <button type="submit" disabled={loading} className="lv-btn-primary w-full min-h-[48px]" data-testid="claim-submit">
            {loading ? "Setting up\u2026" : "Continue"}
          </button>
        </form>
        <div className="mt-10 text-center">
          <Link to="/auth/sign-in" className="text-sm text-lv-muted hover:text-bronze transition-colors tracking-wide">Already have an account? Sign in</Link>
        </div>
      </div>
    </div>
  );
}
